import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import { Button, SectionTitle } from '@presentation/components';
import type { MediaQueryBreakpoint } from '@presentation/styles';
import { BREAKPOINTS, mediaQuery, responsiveFontSize } from '@presentation/styles';
import TextareaAutosize from 'react-textarea-autosize';

export const MOBILE_BREAKPOINT: MediaQueryBreakpoint = 'tablet';

export const Wrapper = styled.div`
  /* display: flex; */
  height: 100%;
  width: 100%;
  overflow: hidden;
  position: relative;
`;

export const Title = styled(SectionTitle)`
  padding: 16px;
`;

export const SideWrapper = styled.div<{ mobileMessagesShown: boolean }>`
  background: ${(props) => props.theme.color.light.default};
  height: 100%;
  overflow: auto;
  border-right: 1px solid ${(props) => props.theme.color.light.shadeLighter};
  border-left: 1px solid ${(props) => props.theme.color.light.shadeLighter};
  width: 300px;

  ${mediaQuery(MOBILE_BREAKPOINT)} {
    width: 100%;
  }
`;

export const ContentWrapper = styled.div<{ mobileMessagesShown: boolean }>`
  display: flex;
  height: 100%;
  flex-direction: column;
  width: 100%;
  background: ${(props) => props.theme.color.light.tint};

  width: calc(100% - 300px);
  position: absolute;
  top: 0;
  right: 0;

  ${mediaQuery(MOBILE_BREAKPOINT)} {
    z-index: 1;
    width: 100%;
    animation: ${(props) =>
        props.mobileMessagesShown ? contentSlideMessagesIn : contentSlideMessagesOut}
      0.2s linear both;
  }
`;

const contentSlideMessagesIn = keyframes`
  0% {
    transform: translateX(100%);
  }
  100% {
    transform: translateX(0%);
  }
`;

const contentSlideMessagesOut = keyframes`
  0% {
    transform: translateX(0%);
  }
  100% {
    transform: translateX(100%);
  }
`;

export const TopWrapper = styled.div`
  height: 80px;
  background: ${(props) => props.theme.color.light.default};
  padding: 16px;
  border-bottom: 1px solid ${(props) => props.theme.color.light.shadeLighter};
  display: flex;
  align-items: center;

  ${mediaQuery(MOBILE_BREAKPOINT)} {
    padding: 8px;
  }
`;

export const ConversationForwardButton = styled.div`
  display: none;
  margin-left: 20px;
  font-size: 20px;
  cursor: pointer;
  ${mediaQuery(MOBILE_BREAKPOINT)} {
    display: flex;
  }
`;

export const ConversationBackButton = styled.div`
  display: none;
  margin-right: 20px;
  font-size: 24px;
  cursor: pointer;
  ${mediaQuery(MOBILE_BREAKPOINT)} {
    display: flex;
  }
`;

export const ConversationDeleteIcon = styled.div`
  margin-left: auto;
  cursor: pointer;
  font-size: 24px;
  height: 24px;
  &:hover {
    color: ${(props) => props.theme.color.red.default};
  }
`;

export const TopUserName = styled.div`
  margin-left: 10px;
  font-size: 16px;
  font-weight: 700;
`;

export const TopLabel = styled.div`
  margin-left: 10px;
`;

export const MessageSectionsWrapper = styled.div`
  flex: 1 0 0;
  overflow: auto;
  padding: 10px 30px;

  ${mediaQuery(BREAKPOINTS.tablet)} {
    padding: 10px 15px;
  }
`;

export const MessageSection = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 30px;
`;

export const MessageSectionTitle = styled.div<{ isToday: string }>`
  padding: 8px 20px;
  border-radius: 5px;
  background: ${(props) =>
    props.isToday === 'true'
      ? props.theme.color.blue.glassShade
      : props.theme.color.light.shadeLighter};
  color: ${(props) =>
    props.isToday === 'true' ? props.theme.color.blue.default : props.theme.color.dark.default};
  font-weight: 600;
  font-size: 14px;
  margin: 0 auto;
  display: inline-block;
  margin-bottom: 20px;
`;

export const MessagesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
`;

export const InputWrapper = styled.div`
  padding: 16px 24px;
  overflow: auto;
  background: ${(props) => props.theme.color.light.default};
  box-shadow: -1px 3px 16px rgba(149, 157, 165, 0.19);
  height: min-content;
  display: flex;
  align-items: center;

  & > *:not(:last-child) {
    margin-right: 20px;
  }
`;

export const Input = styled(TextareaAutosize)`
  padding: 8px 12px;
  font-size: 16px;
  border: 2px solid ${(props) => props.theme.color.light.shade};
  border-radius: 8px;
  flex-grow: 1;

  color: ${(props) => props.theme.color.dark.default} !important;
  background: ${(props) => props.theme.color.light.default};

  &:focus {
    border: 2px solid ${(props) => props.theme.color.blue.default};
  }

  &::placeholder {
    font-style: normal;
    color: ${(props) => props.theme.color.medium.default} !important;
  }
`;

export const InputFileWrapper = styled.div`
  background: white;
  padding: 10px;
  display: inline-block;
  position: relative;
  transition: all 0.3s;
  height: 100px;
  font-size: 12px;
`;

export const InputImage = styled.img`
  height: 100%;
`;

export const InputImageDeleteIcon = styled.div`
  position: absolute;
  bottom: 5px;
  right: 5px;
  padding: 2px 4px;
  font-size: 14px;
  border-radius: 4px;
  cursor: pointer;
  color: ${(props) => props.theme.color.light.default};
  background: ${(props) => props.theme.color.medium.shade};
`;

export const InputIconWrapper = styled.div<{ disabled?: boolean }>`
  width: 24px;
  padding: 0;
  flex-basis: 24px;
  font-size: 24px;
  height: 24px;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
`;

export const UserWrapper = styled.div<{ active: boolean }>`
  display: flex;
  padding: 16px;
  align-items: center;
  border-bottom: 1px solid ${(props) => props.theme.color.light.shadeLighter};
  justify-content: space-between;
  cursor: pointer;
  ${(props) => props.active && `background: ${props.theme.color.blue.glassShade};`}

  &:focus {
    outline: none; /* remove the default focus outline */
    box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.2); /* add a custom focus shadow */
  }

  &:hover {
    background: ${(props) => props.theme.color.blue.glassShade};
  }
`;

export const UserTextWrapper = styled.div`
  display: flex;
  align-items: center;
`;

export const UserName = styled.div`
  font-size: 16px;
  font-weight: 600;
  margin-left: 8px;

  /* ${mediaQuery('small')} {
    display: none;
  } */
`;

export const NotificationDot = styled.div`
  height: 10px;
  width: 10px;
  border-radius: 50%;
  margin-right: 5px;
  background: ${(props) => props.theme.color.red.default};
`;

export const MessageWrapper = styled.div<{ ownMessage: boolean }>`
  display: flex;
  align-items: ${(props) => (props.ownMessage ? 'flex-end' : 'flex-start')};
  flex-direction: column;
  margin-top: 8px;

  &:hover {
    .trash {
      opacity: 1;
    }
  }
  .trash {
    opacity: 0;
    cursor: pointer;
    transition: all 0.2s;
    &:hover {
      color: ${(props) => props.theme.color.red.default};
    }
  }
`;

export const MessageBox = styled.div<{ ownMessage: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: ${(props) => (props.ownMessage ? 'flex-end' : 'flex-start')};
  max-width: 50%;
  gap: 10px;

  ${mediaQuery(BREAKPOINTS.phone)} {
    max-width: 80%;
  }
`;

export const MessageContentWrapper = styled.div<{ ownMessage: boolean }>`
  position: relative;
  background: ${(props) =>
    props.ownMessage ? props.theme.color.blue.default : props.theme.color.light.shadeLighter};

  ${responsiveFontSize(16, 1)};
  border-radius: 6px;
  padding: 8px 16px;
  white-space: pre-wrap;
`;
export const ErrorContentWrapper = styled.div`
  position: relative;
  background: ${(props) => props.theme.color.red.default};
  ${responsiveFontSize(16, 1)};
  border-radius: 6px;
  padding: 8px 16px;
  white-space: pre-wrap;
`;

export const FileContentWrapper = styled.div`
  position: relative;
  background: ${(props) => props.theme.color.light.default};
  border-radius: 4px;
  padding: 6px;
  filter: drop-shadow(0px 0px 14px rgba(0, 0, 0, 0.15));
`;

export const MessageImage = styled.img`
  width: 100%;
  object-fit: cover;
  cursor: pointer;
`;

export const MessageFile = styled(Button)`
  height: auto;
  white-space: normal;
`;

export const MessageText = styled.div<{ ownMessage: boolean }>`
  color: ${(props) =>
    props.ownMessage ? props.theme.color.light.default : props.theme.color.dark.shade};
`;

export const ErrorText = styled.div`
  color: ${(props) => props.theme.color.yellow.default};
`;

export const MessageTrash = styled.div`
  position: absolute;
  left: -30px;
  top: 50%;
  transform: translateY(-40%);
`;

export const TimeSeenRow = styled.div`
  display: flex;
  margin-top: 4px;
  gap: 4px;
`;

export const TimeBox = styled.div`
  font-size: 13px;
  color: ${(props) => props.theme.color.dark.default};
`;

export const StatusBox = styled.span<{ active?: boolean }>`
  text-align: right;

  ${(props) => props.active && `color: ${props.theme.color.blue.default}`}
`;

export const WarningWrapper = styled.div`
  border-top: 1px solid ${(props) => props.theme.color.medium.tint};
  padding: 10px 30px 10px 10px;
  overflow: auto;
  background: ${(props) => props.theme.color.light.shadeLighter};
  height: min-content;
  display: flex;
  align-items: center;
`;
